import moment from "moment";

/**
 * Check if two date ranges overlap
 * @param {string|Date|moment} start1 - Start date of first range
 * @param {string|Date|moment|null} end1 - End date of first range (or null for ongoing)
 * @param {string|Date|moment} start2 - Start date of second range
 * @param {string|Date|moment|null} end2 - End date of second range (or null for ongoing)
 * @returns {boolean} True if ranges overlap
 */
export const doDateRangesOverlap = (start1, end1, start2, end2) => {
  // Convert all dates to moment objects
  const s1 = moment(start1);
  const e1 = end1 ? moment(end1) : moment("9999-12-31"); // Far future date as default
  const s2 = moment(start2);
  const e2 = end2 ? moment(end2) : moment("9999-12-31"); // Far future date as default

  // Check for overlap - one range starts before the other ends
  return s1.isSameOrBefore(e2) && e1.isSameOrAfter(s2);
};

/**
 * Validates that an end date is after a start date
 * @param {string|Date|moment} startDate - Start date
 * @param {string|Date|moment} endDate - End date
 * @returns {boolean} True if end date is after start date
 */
export const isEndDateAfterStartDate = (startDate, endDate) => {
  if (!startDate || !endDate) return true; // If either is missing, validation passes
  return moment(endDate).isSameOrAfter(moment(startDate), "day");
};

/**
 * Checks if a new period overlaps with any existing periods
 * @param {Object} newPeriod - The new period to check
 * @param {string|Date} newPeriod.effective_from - Start date
 * @param {string|Date|null} newPeriod.effective_to - End date (or null)
 * @param {Array} existingPeriods - Array of existing periods
 * @param {string|number|null} excludePeriodId - ID of period to exclude (for edit mode)
 * @returns {boolean} True if there's an overlap
 */
export const hasOverlappingPeriods = (
  newPeriod,
  existingPeriods,
  excludePeriodId = null
) => {
  if (!newPeriod.effective_from) return false;

  return existingPeriods.some((period) => {
    // Skip comparing with itself when editing
    if (period.id === excludePeriodId) return false;

    return doDateRangesOverlap(
      newPeriod.effective_from,
      newPeriod.effective_to,
      period.effective_from,
      period.effective_to
    );
  });
};

/**
 * Function to disable start dates in DatePicker based on existing periods
 * @param {moment} current - The current date being evaluated
 * @param {Array} localPeriods - Array of existing periods
 * @param {Object} selectedPeriod - The currently selected period (for excluding in edit mode)
 * @param {Form} modalForm - The Ant Design Form instance
 * @returns {boolean} - True if the date should be disabled
 */
export const disableStartDates = (
  current,
  localPeriods,
  selectedPeriod,
  modalForm
) => {
  // Don't allow selecting dates that would overlap with existing periods
  // (except the currently edited period)
  for (const period of localPeriods) {
    if (period.id === selectedPeriod?.id) continue;

    const periodStart = moment(period.effective_from);
    const periodEnd = period.effective_to
      ? moment(period.effective_to)
      : moment("9999-12-31");

    // If the current date falls within any existing period range
    if (current.isBetween(periodStart, periodEnd, "day", "[]")) {
      return true; // Disable this date
    }
  }

  // Also check against the end date if it's already selected
  const endDate = modalForm.getFieldValue("effective_to");
  if (endDate && moment(current).isAfter(endDate)) {
    return true; // Disable dates after the end date
  }

  return false; // Enable all other dates
};

/**
 * Function to disable end dates in DatePicker based on existing periods
 * @param {moment} current - The current date being evaluated
 * @param {Array} localPeriods - Array of existing periods
 * @param {Object} selectedPeriod - The currently selected period (for excluding in edit mode)
 * @param {Form} modalForm - The Ant Design Form instance
 * @returns {boolean} - True if the date should be disabled
 */
export const disableEndDates = (
  current,
  localPeriods,
  selectedPeriod,
  modalForm
) => {
  // Don't allow selecting end dates before start date
  const startDate = modalForm.getFieldValue("effective_from");
  if (startDate && moment(current).isBefore(startDate)) {
    return true; // Disable dates before start date
  }

  // Don't allow selecting dates that would overlap with existing periods
  // (except the currently edited period)
  for (const period of localPeriods) {
    if (period.id === selectedPeriod?.id) continue;

    const periodStart = moment(period.effective_from);
    const periodEnd = period.effective_to
      ? moment(period.effective_to)
      : moment("9999-12-31");

    // Check if the current date falls within any existing period range
    if (current.isBetween(periodStart, periodEnd, "day", "[]")) {
      return true; // Disable this date
    }

    // Additional check: prevent creating a period that ends after the start of another period
    if (moment(current).isBefore(periodStart)) {
      continue; // Skip if the current end date is before the start of the other period
    }

    if (
      moment(startDate).isBefore(periodEnd) &&
      moment(current).isAfter(periodStart)
    ) {
      return true; // Prevent creating a period inside another period
    }
  }

  return false; // Enable all other dates
};
